/**
 * All-Storyboards panel — embedded inside the Content Factory page
 * as a tab so users have a single "everything content" destination
 * (see /app/frontend/src/pages/ReviewApprove.jsx).
 *
 * Fetches every storyboard the user owns and renders a flat, sortable
 * card list. This is the recovery path for boards that never went
 * through the automation pipeline — most importantly, "Create Your
 * Own Story" boards, which never land in the Review queue and were
 * previously unreachable once the user navigated away from the
 * detail page.
 *
 * Kept lightweight on purpose — the full-fat channel-grouped view
 * still lives at `/storyboards` (StoryboardList.jsx). This tab only
 * needs to answer "where did my board go?".
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, RefreshCw, ExternalLink, Search } from "lucide-react";
import { listStoryboards } from "@/lib/api";

function timeAgo(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const mins = Math.max(0, (Date.now() - d.getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${Math.round(mins)}m ago`;
  const hrs = mins / 60;
  if (hrs < 24) return `${Math.round(hrs)}h ago`;
  const days = hrs / 24;
  if (days < 30) return `${Math.round(days)}d ago`;
  return d.toLocaleDateString();
}

// Timestamp preference order — the source-of-truth for "when was
// this last touched" varies by pipeline. We fall through so a board
// with a manual edit still sorts to the top even if it never had
// an automation run.
function bestTimestamp(sb) {
  return (
    sb.updated_at ||
    sb.last_activity_at ||
    sb.completed_at ||
    sb.started_at ||
    sb.created_at ||
    ""
  );
}

function StoryboardRow({ sb }) {
  const title = sb.video_name || sb.title || "(untitled)";
  const channel = sb.channel_handle || sb.channel_name || "";
  const sceneCount = sb.total_scenes ?? sb.scenes_count ?? sb.scene_count;
  return (
    <li
      data-testid={`all-storyboards-row-${sb.storyboard_id}`}
      className="border border-zinc-800 rounded-sm bg-zinc-950/40 hover:border-yellow-400/60 hover:bg-zinc-900/60 transition-colors"
    >
      <Link
        to={`/storyboards/${sb.storyboard_id}`}
        className="flex items-center gap-4 px-4 py-3"
      >
        <div className="flex-1 min-w-0">
          <div
            className="text-sm text-white font-semibold truncate"
            data-testid={`all-storyboards-title-${sb.storyboard_id}`}
          >
            {title}
          </div>
          <div className="mt-1 flex items-center gap-3 flex-wrap font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
            {channel && (
              <span className="text-yellow-400/80">{channel}</span>
            )}
            {typeof sceneCount === "number" && sceneCount > 0 && (
              <span>{sceneCount} scenes</span>
            )}
            <span>Updated {timeAgo(bestTimestamp(sb))}</span>
          </div>
        </div>
        <ExternalLink className="w-4 h-4 text-zinc-500 group-hover:text-yellow-400 shrink-0" />
      </Link>
    </li>
  );
}

export default function AllStoryboardsPanel() {
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listStoryboards();
      setBoards(res?.storyboards || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't load storyboards");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Sort newest-updated first. Boards with no timestamp fall to the
  // bottom rather than to the top (as they would with a plain
  // string compare on empty vs full ISO strings).
  const sorted = useMemo(() => {
    return [...boards].sort((a, b) => {
      const ta = bestTimestamp(a);
      const tb = bestTimestamp(b);
      if (!ta && !tb) return 0;
      if (!ta) return 1;
      if (!tb) return -1;
      return tb.localeCompare(ta);
    });
  }, [boards]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sorted;
    return sorted.filter((sb) => {
      const hay = [
        sb.video_name,
        sb.title,
        sb.channel_handle,
        sb.channel_name,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [sorted, query]);

  return (
    <div className="space-y-4" data-testid="all-storyboards-panel">
      <div className="flex flex-wrap items-center gap-3">
        <label className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none" />
          <input
            data-testid="all-storyboards-search"
            type="text"
            placeholder="Search by title or channel…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full bg-black border border-zinc-800 focus:border-yellow-400 outline-none rounded-sm pl-9 pr-3 py-2 text-sm text-zinc-200"
          />
        </label>
        <button
          type="button"
          data-testid="all-storyboards-refresh"
          onClick={load}
          className="font-mono text-[11px] uppercase tracking-[0.25em] px-3 py-2 border-2 border-zinc-700 text-zinc-200 hover:border-cyan-400 hover:text-cyan-200 rounded-sm transition-colors flex items-center gap-2"
        >
          <RefreshCw className="w-3 h-3" />
          refresh
        </button>
        <div className="ml-auto font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">
          {filtered.length} / {boards.length} shown
        </div>
      </div>

      {loading ? (
        <div
          data-testid="all-storyboards-loading"
          className="text-zinc-400 font-mono text-sm flex items-center gap-2"
        >
          <Loader2 className="w-4 h-4 animate-spin" />
          loading storyboards…
        </div>
      ) : boards.length === 0 ? (
        <div
          data-testid="all-storyboards-empty"
          className="border-2 border-dashed border-zinc-700 rounded-sm p-8 text-center"
        >
          <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-500 mb-2">
            // NOTHING HERE YET
          </div>
          <p className="text-zinc-300">
            You haven&apos;t built any storyboards yet. Head to{" "}
            <Link to="/create-story" className="text-yellow-400 hover:underline">
              Create Your Own Story
            </Link>{" "}
            or{" "}
            <Link to="/generate-ideas" className="text-yellow-400 hover:underline">
              Generate Story Ideas
            </Link>{" "}
            to build one.
          </p>
        </div>
      ) : filtered.length === 0 ? (
        <div
          data-testid="all-storyboards-empty-filter"
          className="border-2 border-dashed border-zinc-700 rounded-sm p-6 text-center text-zinc-400 font-mono text-sm"
        >
          No storyboards match &quot;{query}&quot;.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="all-storyboards-list">
          {filtered.map((sb) => (
            <StoryboardRow key={sb.storyboard_id} sb={sb} />
          ))}
        </ul>
      )}
    </div>
  );
}
